<template>
    <jet-dialog-modal :show="showModal" @close="handleCancel">
        <template #title>
            <h3 class="text-xl mb-4">
                <span v-if="user">Edit</span>
                <span v-else>Invite</span>
                User
            </h3>
        </template>

        <template #content>
            <form @submit.prevent="handleSubmit" class="pb-16">
                <div class="form-group">
                    <jet-label for="name">Name</jet-label>
                    <jet-input type="text" v-model="form.name" class="w-full block" />
                    <jet-input-error :message="form.errors.name" />
                </div>

                <div class="form-group">
                    <jet-label for="email">Email</jet-label>
                    <jet-input type="email" v-model="form.email" class="w-full block" />
                    <jet-input-error :message="form.errors.email" />
                </div>

                <div class="form-group">
                    <jet-label for="role">Roles</jet-label>
                    <multiselect class="multi-lg h-7 text-base"
                        v-model="form.roles"
                        :options="roles"
                        valueProp="name"
                        trackBy="name"
                        label="name"
                        :closeOnSelect="false"
                        :canClear="false"
                        mode="tags"
                        :classes="{options: ['capitalize', 'max-h120']}"
                    >
                        <template v-slot:tag="{ option, handleTagRemove, disabled }">
                            <div class="multiselect-tag">
                                {{ option.name }}
                                <span
                                    v-if="!disabled"
                                    class="multiselect-tag-remove"
                                    @mousedown.prevent="handleTagRemove(option, $event)"
                                >
                                    <span class="multiselect-tag-remove-icon"></span>
                                </span>
                            </div>
                        </template>
                     </multiselect>
                    <jet-input-error :message="form.errors.roles" />
                </div>

                <div class="form-group" v-if="form.roles.some(role => role.name === 'picker')">
                    <jet-label for="role">Picker type</jet-label>
                    <multiselect class="multi-lg h-7 text-base"
                        v-model="form.picker_order_type"
                        :options="['All', 'Wholesale', 'Retail']"
                        :canClear="false"
                        :classes="{options: ['capitalize', 'max-h120']}"
                    ></multiselect>
                </div>

                <div class="form-group" v-if="hasLocationRole()">
                    <jet-label for="location_id">Location</jet-label>
                    <select
                        v-model="form.location_id"
                        class="rounded-r rounded-l-none w-full"
                    >
                        <option value="">Produce Express</option>
                        <option
                            v-for="location in locations"
                            :key="location.id"
                            :value="location.id"
                            :selected="user?.location_id == location.id"
                        >
                            {{ location.name + ' | ' + location.city }}
                        </option>

                    </select>
                    <jet-input-error :message="form.errors.location_id" />
                </div>

                <div v-if="user" class="form-group">
                    <toggle id="enabled" v-model="form.enabled" />
                    <jet-label for="enabled" value="Active" class="ml-2 inline-block" />
                </div>

                <input type="submit" class="hidden" />
            </form>
        </template>

        <template #footer class="text-right">
            <jet-secondary-button type="button" class="mr-2" v-show="form.isDirty" @click="handleSubmit">
                <span v-if="user">Save</span>
                <span v-else>Invite</span>
            </jet-secondary-button>

            <jet-tertiary-button @click="handleCancel" >Cancel</jet-tertiary-button>
        </template>
    </jet-dialog-modal>

</template>

<script>
import { useForm } from '@inertiajs/inertia-vue3';

import JetButton from '@/Jetstream/Button';
import JetDangerButton from '@/Jetstream/DangerButton';
import JetInput from '@/Jetstream/Input';
import JetInputError from '@/Jetstream/InputError';
import JetLabel from '@/Jetstream/Label';
import JetSecondaryButton from '@/Jetstream/SecondaryButton';
import JetTertiaryButton from '@/Jetstream/TertiaryButton';
import JetDialogModal from '@/Jetstream/DialogModal';
import Multiselect from '@vueform/multiselect';
import Toggle from '@vueform/toggle';

import '@vueform/multiselect/themes/default.css';
import '@vueform/toggle/themes/default.css';

export default {
    setup(props) {
        const form = useForm({
            id: props.user ? props.user.id : null,
            name: props.user ? props.user.name : null,
            email: props.user ? props.user.email : null,
            roles: props.user ? props.user.roles : [],
            enabled: props.user ? props.user.enabled : 1,
            location_id: props.user ? props.user.location_id : "",
            picker_order_type: props.user ? props.user.picker_order_type : "All",
        });

        return { form }

    },

    components: {
        JetButton,
        JetDangerButton,
        JetSecondaryButton,
        JetTertiaryButton,
        JetInputError,
        JetInput,
        JetLabel,
        JetDialogModal,
        Multiselect,
        Toggle,
    },

    props: ['user', 'showModal', 'roles', 'locations'],

    emits: ['close', 'submitted'],

    methods: {
        handleSubmit () {
            let route = this.user ? this.route('settings.users.update', this.form.id) : this.route('settings.users.store');
            let method = this.user ? 'put' : 'post';

            this.form.submit(method, route, {
                onSuccess: () => {
                    this.$emit('submitted');
                    this.resetForm();
                }
            });
        },

        handleCancel () {
            this.form.reset();
            this.form.clearErrors();
            this.$emit('close');
        },

        resetForm (user) {
            this.form.id = user ? user.id : null;
            this.form.name = user ? user.name : null;
            this.form.email = user ? user.email : null;
            this.form.roles = user ? user.roles.map(r => r.name) : [];
            this.form.enabled = user ? user.enabled : 1;
            this.form.location_id = user ? user.location_id : "";
            this.form.clearErrors();
        },

        hasLocationRole() {
            return this.form.roles.some(role => ['driver', 'location'].includes(role));
        },
    },

    watch: {
        user: {
            immediate: true,
            handler: function (user) {
                this.resetForm(user);
            }
        }
    }
}
</script>

<style>
.max-h120 {
    max-height: 120px;
}
</style>
<style scoped>
.multiselect-tag {
    @apply bg-gray text-gray-darker capitalize;
}
</style>
